import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import JSZip from "jszip";

const HospitalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error, success } = useToast();
  const { user } = useAuth();
  const [hospital, setHospital] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    fetchHospitalDetails();
  }, [id]);

  const fetchHospitalDetails = async () => {
    try {
      setLoading(true);
      const documentsData = await api.getHospitalDocuments(id);

      // Create a basic hospital object with the ID since we can't fetch hospital details
      setHospital(documentsData?.hospital || {});
      setDocuments(documentsData?.documents || []);
    } catch (err) {
      error("Failed to fetch hospital documents");
      console.error("Error fetching documents:", err);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (doc) => {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(doc.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Determine file type from original name
      const fileExtension = doc.original_name.split(".").pop().toLowerCase();
      let mimeType = "application/octet-stream";

      switch (fileExtension) {
        case "pdf":
          mimeType = "application/pdf";
          break;
        case "jpg":
        case "jpeg":
          mimeType = "image/jpeg";
          break;
        case "png":
          mimeType = "image/png";
          break;
        case "doc":
          mimeType = "application/msword";
          break;
        case "docx":
          mimeType =
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
          break;
        case "xls":
          mimeType = "application/vnd.ms-excel";
          break;
        case "xlsx":
          mimeType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
          break;
        default:
          mimeType = "application/octet-stream";
      }

      const blob = new Blob([byteArray], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      // Create download link using a more reliable method
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.original_name;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);
      success(`Downloaded ${doc.original_name}`);
    } catch (err) {
      error("Failed to download file");
      console.error("Download error:", err);
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return "📄";
      case "jpg":
      case "jpeg":
      case "png":
        return "🖼️";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      default:
        return "📁";
    }
  };

  const formatFileSize = (base64String) => {
    const sizeInBytes = (base64String.length * 3) / 4;
    if (sizeInBytes < 1024) return `${sizeInBytes.toFixed(0)} B`;
    if (sizeInBytes < 1024 * 1024)
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const downloadAllFiles = async () => {
    if (documents.length === 0) {
      error("No documents to download");
      return;
    }

    try {
      setDownloadingAll(true);
      const zip = new JSZip();
      const usedFileNames = new Set(); // Track all file names added to zip to ensure uniqueness

      // Add each document to the zip file
      documents.forEach((doc, index) => {
        try {
          // Convert base64 to binary string
          const byteCharacters = atob(doc.base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);

          // Generate unique filename
          let uniqueFileName = doc.original_name;

          // If file name already exists in zip, make it unique
          if (usedFileNames.has(doc.original_name)) {
            const fileExtension = doc.original_name.includes(".")
              ? doc.original_name.substring(doc.original_name.lastIndexOf("."))
              : "";
            const fileNameWithoutExt = doc.original_name.includes(".")
              ? doc.original_name.substring(
                  0,
                  doc.original_name.lastIndexOf(".")
                )
              : doc.original_name;

            // Use document ID if available, otherwise use index
            const uniqueIdentifier = doc.id || index;
            uniqueFileName = `${fileNameWithoutExt}_${uniqueIdentifier}${fileExtension}`;

            // If this unique name is also taken, keep incrementing until we find a unique one
            let counter = 1;
            while (usedFileNames.has(uniqueFileName)) {
              uniqueFileName = `${fileNameWithoutExt}_${uniqueIdentifier}_${counter}${fileExtension}`;
              counter++;
            }
          }

          // Track this file name as used
          usedFileNames.add(uniqueFileName);

          // Add file to zip with unique name
          zip.file(uniqueFileName, byteArray);
        } catch (err) {
          console.error(`Error processing file ${doc.original_name}:`, err);
        }
      });

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: "blob" });

      // Create download link
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Hospital_${hospital.id}_Documents.zip`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);
      success(`Downloaded all ${documents.length} files as zip`);
    } catch (err) {
      error("Failed to download all files");
      console.error("Download all error:", err);
    } finally {
      setDownloadingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="hospital-details-wrapper">
        <div className="hospital-details-page-header">
          <div className="header-content">
            <h1>Hospital Details</h1>
            <p>Loading...</p>
          </div>
          <button onClick={() => navigate("/dashboard")} className="back-btn">
            ← Back to Dashboard
          </button>
        </div>
        <div className="hospital-details-container">
          <div className="loading">Loading hospital details...</div>
        </div>
      </div>
    );
  }

  if (!hospital) {
    return (
      <div className="hospital-details-wrapper">
        <div className="hospital-details-page-header">
          <div className="header-content">
            <h1>Hospital Details</h1>
            <p>Hospital not found</p>
          </div>
          <button onClick={() => navigate("/dashboard")} className="back-btn">
            ← Back to Dashboard
          </button>
        </div>
        <div className="hospital-details-container">
          <div className="error">Hospital not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hospital-details-wrapper">
      <div className="hospital-details-page-header">
        <div className="header-content">
          <h1>Hospital Details</h1>
          <p>
            Hospital ID: {hospital?.id || id} - {hospital?.name}
          </p>
        </div>
        <button onClick={() => navigate("/dashboard")} className="back-btn">
          ← Back to Dashboard
        </button>
      </div>

      <div className="hospital-details-container">
        <div className="hospital-info">
          <div className="info-grid">
            <div className="info-item">
              <label>Hospital ID:</label>
              <span>{hospital?.id || id}</span>
            </div>
            <div className="info-item">
              <label>Name:</label>
              <span>{hospital?.name}</span>
            </div>
            {hospital?.city && (
              <div className="info-item">
                <label>City:</label>
                <span>{hospital?.city}</span>
              </div>
            )}
            {hospital?.address && (
              <div className="info-item">
                <label>Address:</label>
                <span>{hospital?.address}</span>
              </div>
            )}
            {hospital?.status && hospital?.status !== "unknown" && (
              <div className="info-item">
                <label>Status:</label>
                <span className={`status-badge status-${hospital?.status}`}>
                  {hospital?.status}
                </span>
              </div>
            )}
            {hospital?.createdAt && (
              <div className="info-item">
                <label>Created:</label>
                <span>
                  {new Date(hospital?.createdAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="documents-section">
          <div className="documents-header">
            <h2>Documents ({documents.length})</h2>
            {documents.length > 0 && (
              <button
                onClick={downloadAllFiles}
                className="download-all-btn"
                disabled={downloadingAll}
                title="Download all files as ZIP"
              >
                {downloadingAll ? "⏳ Downloading..." : "📦 Download All"}
              </button>
            )}
          </div>

          {documents.length === 0 ? (
            <div className="no-documents">
              <p>No documents found for this hospital.</p>
            </div>
          ) : (
            <div className="file-explorer">
              <div className="file-list">
                {documents.map((doc, index) => (
                  <div key={doc.id || index} className="file-item">
                    <div className="file-icon">
                      {getFileIcon(doc.original_name)}
                    </div>
                    <div className="file-info">
                      <div className="file-name" title={doc.original_name}>
                        {doc.original_name}
                      </div>
                      <div className="file-meta">
                        <span className="file-type">{doc.type}</span>
                        <span className="file-size">
                          {formatFileSize(doc.base64)}
                        </span>
                        <span className="file-date">
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="file-actions">
                      <button
                        onClick={() => downloadFile(doc)}
                        className="download-btn"
                        title="Download file"
                      >
                        ⬇️ Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HospitalDetails;
